import React, { useState } from "react";
import { trackFacebookEvent, trackGA4Event } from "../../services/analytics";

interface WhatsAppButtonProps {
  phoneNumber: string;
  message?: string;
}

// Check if holiday notice should still be shown (expires 5 Jan 2026 at 06:00)
function isHolidayNoticeActive(): boolean {
  const now = new Date();
  const expiryDate = new Date("2026-01-05T06:00:00");
  return now < expiryDate;
}

export function WhatsAppButton({
  phoneNumber,
  message = "Hi, I'm interested in your products",
}: WhatsAppButtonProps) {
  const [showHolidayNotice, setShowHolidayNotice] = useState(false);

  const openWhatsApp = () => {
    // Track WhatsApp click in Facebook Pixel and GA4
    trackFacebookEvent("Contact", {
      content_name: "WhatsApp Chat",
      content_category: "Contact",
    });

    trackGA4Event({
      eventName: "contact",
      eventParams: {
        method: "whatsapp",
      },
    });

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleClick = () => {
    if (isHolidayNoticeActive()) {
      setShowHolidayNotice(true);
    } else {
      openWhatsApp();
    }
  };

  const handleProceed = () => {
    openWhatsApp();
    setShowHolidayNotice(false);
  };

  const handleClose = () => {
    setShowHolidayNotice(false);
  };

  return (
    <>
      {/* Holiday Notice Modal */}
      {showHolidayNotice && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            padding: "1rem",
          }}
          onClick={handleClose}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                background: "#fff3cd",
                color: "#856404",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1.5rem",
                border: "1px solid #ffc107",
              }}
            >
              <strong>Our offices are closed for the holiday season.</strong>
              <br />
              Your message will be received, but processed on 5 January.
            </div>
            <button
              onClick={handleProceed}
              style={{
                width: "100%",
                padding: "0.75rem 1rem",
                background: "#25D366",
                color: "white",
                border: "none",
                borderRadius: "50px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Accept
            </button>
          </div>
        </div>
      )}

      {/* WhatsApp Button */}
      <button
        onClick={handleClick}
        className="whatsapp-button"
        aria-label="Chat on WhatsApp"
        style={{
          position: "fixed",
          bottom: "25px",
          right: "25px",
          width: "60px",
          height: "60px",
          borderRadius: "50%",
          backgroundColor: "#25D366",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(37, 211, 102, 0.4)",
          transition: "all 0.3s ease",
          zIndex: 9999,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow =
            "0 6px 20px rgba(37, 211, 102, 0.6)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow =
            "0 4px 12px rgba(37, 211, 102, 0.4)";
        }}
      >
        {/* WhatsApp Icon SVG */}
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
            fill="white"
          />
        </svg>
      </button>
    </>
  );
}
